import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import Navbar from "../../components/Reports/NavBar/NavBar";
import CarReportTable from "./components/CarReport/CarReportTable";
import FailureReportTable from "./components/FailureReport/FailureReportTable";
import GeoZoneReportTable from "./components/GeoZoneReport/GeoZoneReport";
import UniversalReportTable from "./components/UniversalReport/UniversalReportTable";
import ReportFilters from "../../components/Reports/Filters/ReportFilters";
import { ScheduleTable } from "./components/ScheduleTable/ScheduleTable";
import {
    cpPickupPlanService,
    mapPlannedScheduleToItems,
} from "../../services/planRoute/cpPickupPlan-service";
import type { PlannedScheduleTableRow } from "../../types/api/planRoute/cp.types";
import styles from "./Reporting.module.css";

type ReportFiltersState = {
    [key: string]: {
        search?: string;
        geozone?: string;
        geoBuildMode?: "union" | "single";
        geoAddress?: string;
        locality?: string;
        dateFrom?: string;
        dateTo?: string;
        personType?: "all" | "juridical" | "ip" | "physical";
        contractor?: string;
        car?: string;
    };
};

type SheetItem = {
    key: string;
    name: string;
    sheetName?: string;
};

const sheets: SheetItem[] = [
    { name: "Отчет по КП", key: "list", sheetName: "Список КП" },
    { name: "Отчет по автомобилю", key: "car", sheetName: "Мусоровозы" },
    { name: "Отчет в рамках геозоны", key: "geo", sheetName: "Геозоны" },
    { name: "Отчет в рамках автомобиля", key: "frameCar", sheetName: "Автомобили" },
    { name: "Отчет в рамках сбоев", key: "failure", sheetName: "Сбои" },
    { name: "Расписание", key: "schedule" },
];

const FILE_URL =
    "/reports/++Иволг_р_для_Автоматизации+пояснения_по_объемам.xlsx";

const Reporting: React.FC = () => {
    const location = useLocation();
    const sheetFromQuery = new URLSearchParams(location.search).get("sheet");
    const initialSheetKey =
        sheetFromQuery && sheets.some(s => s.key === sheetFromQuery)
            ? sheetFromQuery
            : sheets[0].key;

    const [activeSheet, setActiveSheet] = useState<string>(initialSheetKey);
    const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
    const [error, setError] = useState("");

    const [sheetCache, setSheetCache] = useState<Record<string, any[]>>({});
    const [filters, setFilters] = useState<ReportFiltersState>({});

    const [scheduleRows, setScheduleRows] = useState<PlannedScheduleTableRow[]>([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleError, setScheduleError] = useState<string | null>(null);

    const handleSearchChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                search: value,
            },
        }));
    };

    const handleGeozoneChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                geozone: value,
            },
        }));
    };

    const handleGeoBuildModeChange = (value: "union" | "single") => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                geoBuildMode: value,
            },
        }));
    };

    const handleGeoAddressChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                geoAddress: value,
            },
        }));
    };

    const handleLocalityChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                locality: value,
            },
        }));
    };

    const handleDateFromChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                dateFrom: value,
            },
        }));
    };

    const handleDateToChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                dateTo: value,
            },
        }));
    };

    const handlePersonTypeChange = (
        value: "all" | "juridical" | "ip" | "physical"
    ) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                personType: value,
            },
        }));
    };

    const handleContractorChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                contractor: value,
            },
        }));
    };

    const handleCarChange = (value: string) => {
        setFilters(prev => ({
            ...prev,
            [activeSheet]: {
                ...prev[activeSheet],
                car: value,
            },
        }));
    };

    useEffect(() => {
        const loadWorkbook = async () => {
            try {
                const response = await fetch(FILE_URL);
                if (!response.ok) throw new Error();

                const buffer = await response.arrayBuffer();
                const wb = XLSX.read(buffer, { type: "array" });
                setWorkbook(wb);

                const cache: Record<string, any[]> = {};
                sheets.forEach(s => {
                    if (!s.sheetName) return;
                    const sheet = wb.Sheets[s.sheetName];
                    cache[s.key] = sheet
                        ? XLSX.utils.sheet_to_json(sheet, { defval: "" })
                        : [];
                });
                setSheetCache(prev => ({
                    ...prev,
                    ...cache,
                }));
            } catch {
                setError("Ошибка загрузки Excel");
            }
        };

        loadWorkbook();
    }, []);

    useEffect(() => {
        if (activeSheet !== "schedule") return;


        let cancelled = false;
        setScheduleLoading(true);
        setScheduleError(null);

        cpPickupPlanService
            .getPlannedSchedule()
            .then(res => {
                if (!cancelled) {
                    setScheduleRows(mapPlannedScheduleToItems(res.data));
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const ax = err as { response?: { data?: unknown }; message?: string };
                const body = ax.response?.data;
                const msg =
                    typeof body === "string" && body.trim()
                        ? body
                        : ax.message || "Не удалось загрузить расписание";
                setScheduleError(msg);
                setScheduleRows([]);
            })
            .finally(() => {
                if (!cancelled) setScheduleLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [activeSheet]);

    const currentData = sheetCache[activeSheet] || [];
    const currentFilters = filters[activeSheet] || {};

    const filteredData = currentData.filter(row => {
        const searchOk = currentFilters.search
            ? Object.values(row).some(v =>
                String(v)
                    .toLowerCase()
                    .includes(currentFilters.search!.toLowerCase())
            )
            : true;

        const geozoneOk =
            activeSheet === "geo" &&
            currentFilters.geozone &&
            currentFilters.geozone !== "all"
                ? (row as any).geoZone === currentFilters.geozone
                : true;

        const concreteAddressOk = currentFilters.geoAddress
            ? Object.values(row).some(v =>
                String(v)
                    .toLowerCase()
                    .includes(currentFilters.geoAddress!.toLowerCase())
            )
            : true;

        return searchOk && geozoneOk && concreteAddressOk;
    });

    return (
        <div className={styles.reporting}>
            <Navbar
                reports={sheets.map(s => ({ name: s.name, key: s.key }))}
                activeReport={activeSheet}
                onSelect={setActiveSheet}
            />

            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    overflow: "auto",
                }}
            >
                {!error && activeSheet !== "schedule" && (
                    <ReportFilters
                        activeSheet={activeSheet}
                        currentFilters={currentFilters}
                        sheetCache={sheetCache}
                        onSearchChange={handleSearchChange}
                        onGeozoneChange={handleGeozoneChange}
                        onGeoBuildModeChange={handleGeoBuildModeChange}
                        onGeoAddressChange={handleGeoAddressChange}
                        onLocalityChange={handleLocalityChange}
                        onDateFromChange={handleDateFromChange}
                        onDateToChange={handleDateToChange}
                        onPersonTypeChange={handlePersonTypeChange}
                        onContractorChange={handleContractorChange}
                        onCarChange={handleCarChange}
                    />
                )}

                {activeSheet === "schedule" && (
                    <ScheduleTable
                        title="Расписание"
                        data={scheduleRows}
                        loading={scheduleLoading}
                        error={scheduleError}
                    />
                )}

                {!error && workbook && activeSheet !== "schedule" && (
                    activeSheet === "geo" ? (
                        <GeoZoneReportTable data={filteredData} />
                    ) : activeSheet === "frameCar" ? (
                        <CarReportTable data={filteredData as any} />
                    ) : activeSheet === "failure" ? (
                        <FailureReportTable data={filteredData as any} />
                    ) : (
                        <UniversalReportTable
                            data={filteredData}
                            columns={filteredData[0] ? Object.keys(filteredData[0]) : []}
                        />
                    )
                )}
            </div>
        </div>
    );
};

export default Reporting;